import { createUseStyles } from "react-jss";
import { ITheme } from "../../constants/theme";
import {
  BreakPointDesktop,
  BreakPointMobile,
  BreakPointTablet,
} from "../../constants/dimension";
import tagIcon from "../../assets/subscription/tag_icon.svg";
import Ellipse from "../../assets/subscription/ellipse.svg";

const MOBILE_IMAGE_COLUMN = 110;

export const useStyles = createUseStyles((theme: ITheme) => ({
  root: {
    boxSizing: "border-box",
    position: "relative",
    zIndex: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    overflow: "scroll",
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    background: "#000",
    paddingTop: 56,
    gap: theme.spacing.space16,
    [BreakPointMobile]: {
      overflow: "scroll",
    },
    [BreakPointTablet]: {
      overflow: "hidden",
      paddingTop: 96,
      gap: 92,
      alignItems: "center",
    },
    [BreakPointDesktop]: {
      gap: theme.spacing.space48,
      alignItems: "center",
    },
  },

  background: {
    position: "fixed",
    zIndex: -1,
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    "&.featured": {
      background:
        "radial-gradient(circle 25vw at 50% 20%, #3D70B7 0%, #000 100%)",
    },
    [BreakPointDesktop]: {
      background:
        "radial-gradient(circle 16vw at 30% 57%, #3D70B7 0%, #000 100%)",
      "&.featured": {
        background:
          "radial-gradient(circle 16vw at 30% 57%, #3D70B7 0%, #000 100%)",
      },
    },
    [BreakPointTablet]: {
      background: "#000000",
    },
  },

  header: {
    position: "fixed",
    zIndex: theme.zIndex.normal + 1,
    top: theme.spacing.space12,
    left: theme.spacing.space8,
    cursor: "pointer",
    display: "flex",
    [BreakPointDesktop]: {
      borderRadius: 20,
      background: "rgba(255, 255, 255, 0.10)",
      backdropFilter: "blur(24.545454025268555px)",
      width: 96,
      height: 96,
      top: 40,
      left: 90,
      alignItems: "center",
      justifyContent: "center",
    },
  },

  iconHeader: {
    padding: theme.spacing.space8,
  },

  headerText: {
    textAlign: "center",
    padding: `0 ${theme.spacing.space16}px`,
  },

  mainText: {
    textTransform: "uppercase",
    fontSize: "20px !important",
    lineHeight: "28px !important",
    color: "#0AF6FB !important",
    [BreakPointTablet]: {
      fontSize: "45px !important",
      lineHeight: "63px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "60px !important",
      lineHeight: "84px !important",
    },
  },

  subText: {
    fontSize: "18px !important",
    lineHeight: "25.2px !important",
    [BreakPointTablet]: {
      fontSize: "32px !important",
      lineHeight: "44.8px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "36px !important",
      lineHeight: "50.4px !important",
    },
  },

  bottomBar: {
    boxSizing: "border-box",
    position: "fixed",
    width: "100%",
    zIndex: theme.zIndex.normal + 1,
    left: 0,
    bottom: 0,
    background:
      "linear-gradient(0deg, #191919 0%, #191919 100%), rgba(255, 255, 255, 0.10)",
    borderTop: "1px solid rgba(255, 255, 255, 0.10)",
    padding: `${theme.spacing.space8}px ${theme.spacing.space16}px`,
    [BreakPointTablet]: {
      padding: `${theme.spacing.space16}px ${theme.spacing.space24}px`,
    },
    [BreakPointDesktop]: {
      padding: `${theme.spacing.space24}px 90px`,
    },
  },

  contentSection: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing.space12,
    padding: `0 ${theme.spacing.space16}px`,
    paddingBottom: 70,
    [BreakPointTablet]: {
      display: "grid",
      gridTemplateColumns: "2fr 1fr",
      alignItems: "stretch",
      gap: theme.spacing.space24,
      padding: `0 ${theme.spacing.space24}px`,
      paddingBottom: 70,
      "&.founder": {
        display: "flex",
        alignItems: "center",
      },
    },
    [BreakPointDesktop]: {
      display: "grid",
      gridTemplateColumns: "1.5fr 0.5fr",
      alignItems: "stretch",
      gap: theme.spacing.space40,
      width: "auto",
      padding: 0,
      paddingBottom: 70,
      marginTop: "auto",
      marginBottom: "auto",
      "&.founder": {
        display: "flex",
        alignItems: "center",
      },
    },
    [BreakPointMobile]: {
      "@media (orientation: landscape) and (max-width: 931px)": {
        width: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
  },

  /* --- Smart Console Bundle card ------------------------------------- */

  bundleConsoleSection: {
    position: "relative",
    overflow: "hidden",
    borderRadius: theme.radius.medium,
    padding: theme.spacing.space12,
    border: "3px solid transparent",
    backgroundImage:
      "linear-gradient(167deg, rgba(54, 62, 81, 0.60) 25.94%, rgba(25, 30, 38, 0.60) 68.26%), linear-gradient(101deg, rgba(24, 119, 235, 0.50) 3.41%, rgba(14, 67, 133, 0.50) 92.74%), linear-gradient(180deg, #1F2943 0%, rgba(62, 66, 75, 0.24) 100%), linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.04) 100%)",
    backgroundOrigin: "padding-box, padding-box, padding-box, border-box",
    backgroundClip: "padding-box, padding-box, padding-box, border-box",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "-2%",
      bottom: 0,
      width: "95%",
      height: "100%",
      backgroundImage: `url("${Ellipse}")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "left bottom",
      backgroundSize: "95% auto",
      pointerEvents: "none",
    },
    [BreakPointMobile]: {
      "&::before": {
        display: "none",
      },
    },
    [BreakPointTablet]: {
      padding: theme.spacing.space16,
      "&::before": {
        left: 0,
        width: "95%",
      },
    },
    [BreakPointDesktop]: {
      padding: theme.spacing.space32,
    },
  },

  headerTag: {
    position: "absolute",
    zIndex: theme.zIndex.normal,
    left: theme.spacing.space8,
    top: theme.spacing.space8,
    width: 150,
    height: 26,
    backgroundImage: `url("${tagIcon}")`,
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    display: "flex",
    alignItems: "center",
    [BreakPointTablet]: {
      left: theme.spacing.space8,
      top: theme.spacing.space12,
      width: 200,
      height: 32,
    },
    [BreakPointDesktop]: {
      left: theme.spacing.space12,
      top: theme.spacing.space20,
      width: 260,
      height: 41,
    },
  },

  tagText: {
    paddingLeft: theme.spacing.space8,
    fontSize: "10px !important",
    lineHeight: "14px !important",
    [BreakPointTablet]: {
      fontSize: "12px !important",
      lineHeight: "16.8px !important",
    },
    [BreakPointDesktop]: {
      paddingLeft: theme.spacing.space12,
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
    },
  },

  bundleConsoleContent: {
    position: "relative",
    zIndex: theme.zIndex.normal,
    display: "grid",
    gridTemplateColumns: `${MOBILE_IMAGE_COLUMN}px 1fr`,
    gridTemplateAreas: '"image text" "image price"',
    alignItems: "center",
    columnGap: theme.spacing.space12,
    rowGap: theme.spacing.space12,
    [BreakPointTablet]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      paddingTop: theme.spacing.space32,
    },
    [BreakPointDesktop]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      paddingTop: theme.spacing.space32,
    },
  },

  bundleConsoleText: {
    gridArea: "text",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    [BreakPointTablet]: {
      alignItems: "center",
      textAlign: "center",
    },
    [BreakPointDesktop]: {
      alignItems: "center",
      textAlign: "center",
    },
  },

  bundleTitle: {
    fontSize: "16px !important",
    lineHeight: "22.4px !important",
    [BreakPointTablet]: {
      fontSize: "32px !important",
      lineHeight: "44.8px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "40px !important",
      lineHeight: "56px !important",
    },
  },

  bundleSubtitle: {
    fontSize: "12px !important",
    lineHeight: "16.8px !important",
    [BreakPointTablet]: {
      fontSize: "14px !important",
      lineHeight: "19.6px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "18px !important",
      lineHeight: "25.2px !important",
    },
  },

  bundleConsoleImage: {
    gridArea: "image",
    width: 126,
    height: 110,
    aspectRatio: 181 / 155,
    justifySelf: "center",
    marginTop: theme.spacing.space16,
    "@media (max-width: 359px)": {
      width: "100%",
      maxWidth: 126,
      height: "auto",
    },
    [BreakPointTablet]: {
      boxSizing: "border-box",
      width: "100%",
      maxWidth: 377.307,
      height: "auto",
      aspectRatio: 377.31 / 323.31,
    },
    [BreakPointDesktop]: {
      width: 485,
      height: 415.338,
      maxHeight: "calc(100vh - 600px)",
      minHeight: 240,
      objectFit: "contain",
      padding: "0 54px",
    },
  },

  bundleConsoleBottom: {
    display: "contents",
    [BreakPointTablet]: {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "end",
      gap: theme.spacing.space16,
      marginTop: -46,
      "@media (max-width: 730px)": {
        gap: theme.spacing.space8,
      },
    },
    [BreakPointDesktop]: {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "end",
      gap: theme.spacing.space16,
      marginTop: -66,
    },
  },

  launchOffer: {
    gridArea: "offer",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.space8,
    background: "#161616",
    borderRadius: theme.radius.small,
    border: "1.603px solid #0AF6FB",
    padding: theme.spacing.space8,
    [BreakPointMobile]: {
      width: "fit-content",
      maxWidth: "100%",
      minWidth: 0,
      justifySelf: "end",
      gap: theme.spacing.space4,
      border: "1px solid #0AF6FB",
      padding: theme.spacing.space6,
    },
    [BreakPointTablet]: {
      minWidth: 0,
      flexShrink: 1,
    },
  },

  launchOfferLabels: {
    minWidth: 0,
  },

  launchOfferDetails: {
    minWidth: 0,
  },

  flashIcon: {
    [BreakPointMobile]: {
      width: 22,
      flexShrink: 0,
    },
  },

  launchLabel: {
    [BreakPointMobile]: {
      fontSize: `0.5rem !important`,
      lineHeight: "12px !important",
    },
    [BreakPointTablet]: {
      fontSize: "10px !important",
      lineHeight: "14px !important",
    },
  },

  offerLabel: {
    [BreakPointMobile]: {
      fontSize: "13px !important",
      lineHeight: "14px !important",
    },
    [BreakPointTablet]: {
      fontSize: "14px !important",
      lineHeight: "19.6px !important",
    },
  },

  oldPrice: {
    [BreakPointMobile]: {
      fontSize: "0.5rem !important",
      lineHeight: "11.2px !important",
    },
    [BreakPointTablet]: {
      fontSize: "8px !important",
      lineHeight: "11.2px !important",
    },
  },

  perMonth: {
    [BreakPointMobile]: {
      fontSize: `0.5rem !important`,
    },
    [BreakPointTablet]: {
      fontSize: "10px !important",
      lineHeight: "14px !important",
    },
  },

  afterPeriod: {
    [BreakPointMobile]: {
      fontSize: `${7 / 16}rem !important`,
      lineHeight: "11.2px !important",
    },
    [BreakPointTablet]: {
      fontSize: `${8 / 16}rem !important`,
      lineHeight: "11.2px !important",
    },
  },

  dot: {
    borderRadius: "100%",
    width: theme.spacing.space6,
    height: theme.spacing.space6,
    background: "#0AF6FB",
    flexShrink: 0,
    [BreakPointMobile]: {
      width: theme.spacing.space4,
      height: theme.spacing.space4,
    },
  },

  launchOfferPriceContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing.space4,
  },

  launchOfferPrice: {
    color: "#0AF6FB !important",
    [BreakPointMobile]: {
      fontSize: "12px !important",
      lineHeight: "12px !important",
    },
  },

  strikeThrough: {
    position: "relative",
    display: "inline-block",

    "&::after": {
      content: '""',
      position: "absolute",
      left: "-5%",
      top: "45%",
      width: "110%",
      height: "1px",
      background: "#fff",
      transform: "rotate(15deg)",
      transformOrigin: "center",
    },
  },

  launchOfferBtnContainer: {
    gridArea: "price",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.space8,
    [BreakPointTablet]: {
      alignItems: "flex-end",
      justifyContent: "center",
      flexShrink: 0,
      marginLeft: "auto",
      marginRight: 0,
    },
    [BreakPointDesktop]: {
      alignItems: "flex-end",
      justifyContent: "center",
      marginLeft: "auto",
      marginRight: 0,
    },
  },

  startingAt: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing.space4,
    [BreakPointTablet]: {
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 2,
    },
    [BreakPointDesktop]: {
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 0,
    },
  },

  startingAtLabel: {
    fontSize: "12px !important",
    lineHeight: "16.8px !important",
    [BreakPointTablet]: {
      fontSize: "14px !important",
      lineHeight: "19.6px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "20px !important",
      lineHeight: "28px !important",
    },
  },

  bundlePrice: {
    fontSize: "16px !important",
    lineHeight: "22.4px !important",
    [BreakPointTablet]: {
      fontSize: "32px !important",
      lineHeight: "30.8px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "40px !important",
      lineHeight: "56px !important",
    },
  },

  /* --- Cloud Console Access card -------------------------------------- */

  planCard: {
    display: "grid",
    gridTemplateColumns: `${MOBILE_IMAGE_COLUMN}px 1fr`,
    gridTemplateAreas: '"image text" "image bottom"',
    alignItems: "center",
    columnGap: theme.spacing.space12,
    rowGap: theme.spacing.space12,
    padding: theme.spacing.space12,
    borderRadius: theme.radius.medium,
    border: "3px solid transparent",
    backgroundImage:
      "linear-gradient(180deg, #1B2237 0%, #12172A 100%), linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.02) 100%)",
    backgroundOrigin: "padding-box, border-box",
    backgroundClip: "padding-box, border-box",
    [BreakPointTablet]: {
      flex: "1 1 0",
      minWidth: 0,
      gridTemplateColumns: "1fr",
      gridTemplateAreas: '"text" "image" "bottom"',
      justifyItems: "center",
      alignContent: "space-between",
      rowGap: theme.spacing.space12,
      padding: theme.spacing.space16,
      "&.featured": {
        flex: "0 0 auto",
        width: 512,
        maxWidth: "100%",
        padding: theme.spacing.space24,
        rowGap: theme.spacing.space24,
        border: "none",
        background:
          "linear-gradient(180deg, #1F2943 0%, rgba(62, 66, 75, 0.24) 100%)",
        "& $planTitle": {
          fontSize: "32px !important",
          lineHeight: "44.8px !important",
        },
        "& $planSubtitle": {
          fontSize: "14px !important",
          lineHeight: "19.6px !important",
        },
        "& $planPrice": {
          fontSize: "32px !important",
          lineHeight: "44.8px !important",
        },
        "& $perMonth": {
          fontSize: "16px !important",
          lineHeight: "22.4px !important",
        },
        "& $cloudConsoleImg": {
          width: 210.169,
          height: 291.473,
          aspectRatio: 106 / 147,
        },
      },
    },
    [BreakPointDesktop]: {
      flex: "1 1 0",
      gridTemplateColumns: "1fr",
      gridTemplateAreas: '"text" "image" "bottom"',
      justifyItems: "center",
      alignContent: "space-between",
      rowGap: theme.spacing.space16,
      padding: theme.spacing.space24,
      "&.featured": {
        flex: "0 0 auto",
        width: 660,
        maxWidth: "100%",
        padding: theme.spacing.space48,
        rowGap: theme.spacing.space24,
        border: "none",
        background:
          "linear-gradient(180deg, #1F2943 0%, rgba(62, 66, 75, 0.24) 100%)",
        "& $planTitle": {
          fontSize: "40px !important",
          lineHeight: "56px !important",
        },
        "& $planSubtitle": {
          fontSize: "18px !important",
          lineHeight: "25.2px !important",
        },
        "& $planPrice": {
          fontSize: "40px !important",
          lineHeight: "56px !important",
        },
        "& $perMonth": {
          fontSize: "24px !important",
          lineHeight: "33.6px !important",
        },
        "& $cloudConsoleImg": {
          width: 295,
          height: 435,
          maxHeight: "calc(100vh - 680px)",
          minHeight: 260,
          aspectRatio: 59 / 87,
        },
      },
    },
  },

  planText: {
    gridArea: "text",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing.space2,
    textAlign: "left",
    [BreakPointTablet]: {
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing.space4,
      textAlign: "center",
    },
    [BreakPointDesktop]: {
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing.space4,
      textAlign: "center",
    },
  },

  planTitle: {
    fontSize: "16px !important",
    lineHeight: "22.4px !important",
    [BreakPointDesktop]: {
      fontSize: "32px !important",
      lineHeight: "32px !important",
    },
    [BreakPointTablet]: {
      fontSize: "18px !important",
      lineHeight: "25.2px !important",
    },
  },

  planSubtitle: {
    fontSize: "12px !important",
    lineHeight: "16.8px !important",
    [BreakPointTablet]: {
      fontSize: "11px !important",
      lineHeight: "15.4px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "14px !important",
      lineHeight: "19.6px !important",
    },
  },

  cloudConsoleImg: {
    gridArea: "image",
    objectFit: "contain",
    justifySelf: "center",
    width: 120,
    height: 120,
    [BreakPointTablet]: {
      width: "100%",
      maxWidth: 261,
      height: "auto",
      aspectRatio: 71 / 105,
    },
    [BreakPointDesktop]: {
      width: 306,
      height: 448,
      maxHeight: "calc(100vh - 566px)",
      minHeight: 260,
      aspectRatio: 125 / 183,
    },
  },

  planBottom: {
    gridArea: "bottom",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing.space8,
    [BreakPointTablet]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    [BreakPointDesktop]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },

  planPriceGroup: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing.space4,
    [BreakPointTablet]: {
      display: "block",
    },
    [BreakPointDesktop]: {
      display: "block",
    },
  },

  planPrice: {
    fontSize: "14px !important",
    lineHeight: "19.6px !important",
    [BreakPointTablet]: {
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "20px !important",
      lineHeight: "28px !important",
    },
  },

  buyBtn: {
    fontSize: "12px !important",
    fontWeight: `${theme.fontWeight.medium} !important`,
    borderRadius: `${theme.radius.large}px`,
    [BreakPointTablet]: {
      fontSize: "10px !important",
      fontWeight: `${theme.fontWeight.medium} !important`,
    },
    [BreakPointDesktop]: {
      fontSize: `${theme.spacing.space16}px !important`,
      fontWeight: `${theme.fontWeight.semiBold} !important`,
    },
  },
}));
